import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import PlantForm from './PlantForm';
import PlantCard from './PlantCard';
import MonthlyDataModal from './MonthlyDataModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch } = FiIcons;

const PlantManagement = () => {
  const { plants } = usePVData();
  const [showForm, setShowForm] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredPlants = plants.filter(
    plant =>
      plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plant.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plant.identifier.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPlant = () => {
    setEditingPlant(null);
    setShowForm(true);
  };

  const handleEditPlant = (plant) => {
    setEditingPlant(plant);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingPlant(null);
  };

  const handleMonthlyData = (plant) => {
    setSelectedPlant(plant);
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Anlagen verwalten</h1>
          <p className="text-gray-600 mt-1">PV-Anlagen hinzufügen und bearbeiten</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddPlant}
          className="bg-primary-500 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2 shadow-lg"
        >
          <SafeIcon icon={FiPlus} />
          <span>Neue Anlage</span>
        </motion.button>
      </motion.div>

      {/* Suchleiste */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative"
      >
        <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Anlagen durchsuchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        />
      </motion.div>

      {/* Anlagen Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
      >
        <AnimatePresence>
          {filteredPlants.map((plant, index) => (
            <motion.div
              key={plant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.1 }}
            >
              <PlantCard plant={plant} onEdit={handleEditPlant} onMonthlyData={handleMonthlyData} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {filteredPlants.length === 0 && !showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8 md:py-12"
        >
          <div className="text-gray-400 text-4xl md:text-6xl mb-4">
            <SafeIcon icon={FiSearch} className="mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm ? 'Keine Anlagen gefunden' : 'Keine Anlagen vorhanden'}
          </h3>
          <p className="text-gray-600">
            {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff' : 'Fügen Sie Ihre erste PV-Anlage hinzu'}
          </p>
        </motion.div>
      )}

      {/* Plant Form Modal */}
      <AnimatePresence>
        {showForm && (
          <PlantForm plant={editingPlant} onClose={handleCloseForm} />
        )}
      </AnimatePresence>

      {/* Monthly Data Modal */}
      <AnimatePresence>
        {selectedPlant && (
          <MonthlyDataModal plant={selectedPlant} onClose={() => setSelectedPlant(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlantManagement;